import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { JobsShell } from '../components/jobs/JobsShell';
import { formatApiError } from '../contexts/JobseekerAuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

export default function JobsResetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [validating, setValidating] = useState(true);
  const [validEmail, setValidEmail] = useState(null);
  const [tokenError, setTokenError] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setTokenError('No reset token in the link.');
      setValidating(false);
      return;
    }
    (async () => {
      try {
        const res = await axios.get(`${API}/api/jobs/auth/validate-reset-token/${token}`);
        setValidEmail(res.data.email);
      } catch (err) {
        setTokenError(formatApiError(err));
      } finally {
        setValidating(false);
      }
    })();
  }, [token]);

  const pwStrong = password.length >= 8 && /[A-Za-z]/.test(password) && /[0-9]/.test(password);
  const canSubmit = pwStrong && password === confirm;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!canSubmit) {
      setError('Passwords do not match or do not meet requirements.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/jobs/auth/reset-password`, {
        reset_token: token,
        new_password: password,
      });
      setSuccess(true);
      setTimeout(() => navigate('/jobs/login'), 2500);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <JobsShell showSigninLink={false}>
      <div className="max-w-md mx-auto px-4 py-14" data-testid="jobs-reset-password-page">
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8">
          {validating ? (
            <div className="text-center py-8" data-testid="reset-validating">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto" />
              <p className="text-slate-600 mt-3 text-sm">Verifying your link…</p>
            </div>
          ) : tokenError ? (
            <div className="text-center" data-testid="reset-invalid-token">
              <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto" />
              <h1 className="mt-4 text-xl font-extrabold text-slate-900">Link not valid</h1>
              <p className="text-slate-600 mt-2 text-sm">{tokenError}</p>
              <Link
                to="/jobs/forgot-password"
                className="mt-6 inline-block px-5 py-2.5 bg-blue-700 hover:bg-blue-800 text-white font-bold rounded-lg"
              >
                Request a new link
              </Link>
            </div>
          ) : success ? (
            <div className="text-center" data-testid="reset-success">
              <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" />
              <h1 className="mt-4 text-2xl font-extrabold text-slate-900">Password updated</h1>
              <p className="text-slate-600 mt-2 text-sm">Redirecting you to sign in…</p>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-extrabold text-slate-900">Set a new password</h1>
              <p className="text-slate-600 text-sm mt-1">
                For <span className="font-semibold">{validEmail}</span>
              </p>

              {error && (
                <div className="mt-4 p-3 bg-rose-50 border border-rose-200 text-rose-700 text-sm rounded-lg" data-testid="reset-error">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wide text-slate-700 mb-1.5">New password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoFocus
                    className="input-base"
                    data-testid="reset-password-input"
                  />
                  {password && !pwStrong && (
                    <p className="text-xs text-amber-700 mt-1">8+ chars, letters and numbers.</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wide text-slate-700 mb-1.5">Confirm password</label>
                  <input
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    className="input-base"
                    data-testid="reset-confirm-input"
                  />
                  {confirm && password !== confirm && (
                    <p className="text-xs text-rose-700 mt-1">Passwords don't match.</p>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={submitting || !canSubmit}
                  data-testid="reset-submit-btn"
                  className="w-full px-5 py-3 bg-blue-700 hover:bg-blue-800 disabled:opacity-60 text-white font-bold rounded-lg shadow-md flex items-center justify-center gap-2"
                >
                  {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Updating…</> : 'Update password'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
      <style>{`
        .input-base {
          width: 100%; padding: 10px 12px; border: 2px solid #e2e8f0;
          border-radius: 10px; font-size: 15px; background: #fff;
        }
        .input-base:focus { outline: none; border-color: #2563eb;
          box-shadow: 0 0 0 4px rgba(37,99,235,0.12); }
      `}</style>
    </JobsShell>
  );
}
